import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Plus, Trash2, Building2, ListChecks, BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Catálogos editables por contrato:
 * - Servicios (BARRIDO MANUAL, RECOGIDA RSU, ...)
 * - Categorías (PEÓN, OFICIAL 1ª, ...)
 * - Flota y personal: solo lectura aquí (se gestionan en /fleet y /personnel)
 */
export default function ContractCatalogsTab({ contracts }) {
  const { authAxios } = useAuth();
  const [contractId, setContractId] = useState(contracts[0]?.id || "");
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newSvc, setNewSvc] = useState("");
  const [newCat, setNewCat] = useState("");

  const load = async (cid) => {
    if (!cid) return;
    setLoading(true);
    try {
      const r = await authAxios.get(`/contracts/${cid}/catalog`);
      setCatalog(r.data);
    } catch {
      toast.error("No se pudo cargar el catálogo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(contractId); }, [contractId]);

  const addItem = async (entity, value) => {
    if (!value.trim()) return;
    try {
      const r = await authAxios.post(`/contracts/${contractId}/catalog/${entity}`, { value: value.trim() });
      setCatalog(c => ({ ...c, [entity]: r.data[entity] }));
      if (entity === "services") setNewSvc("");
      if (entity === "categories") setNewCat("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al añadir");
    }
  };

  const removeItem = async (entity, value) => {
    if (!window.confirm(`¿Eliminar "${value}" del catálogo?`)) return;
    try {
      const r = await authAxios.delete(`/contracts/${contractId}/catalog/${entity}/${encodeURIComponent(value)}`);
      setCatalog(c => ({ ...c, [entity]: r.data[entity] }));
    } catch {
      toast.error("Error al eliminar");
    }
  };

  return (
    <div className="space-y-5 animate-fade-in-up">
      <div className="card-white p-6">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="w-10 h-10 bg-[#E6F4EA] rounded-xl flex items-center justify-center">
            <ListChecks size={20} className="text-[#007934]" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-900">Catálogos por contrato</h2>
            <p className="text-sm text-slate-500">Servicios y categorías disponibles en los dropdowns al editar partes de trabajo.</p>
          </div>
          <select
            data-testid="catalog-contract-select"
            value={contractId}
            onChange={e => setContractId(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-xl text-sm font-semibold bg-white min-w-[280px]"
          >
            {contracts.map(c => (
              <option key={c.id} value={c.id}>{c.code ? `${c.code} — ` : ""}{c.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-10 text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando catálogo...
          </div>
        ) : catalog ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <CatalogList
              title="Servicios"
              icon={Building2}
              items={catalog.services}
              newValue={newSvc}
              setNewValue={setNewSvc}
              onAdd={() => addItem("services", newSvc)}
              onRemove={(v) => removeItem("services", v)}
              placeholder="ej: RECOGIDA PUERTA A PUERTA"
            />
            <CatalogList
              title="Categorías"
              icon={BadgeCheck}
              items={catalog.categories}
              newValue={newCat}
              setNewValue={setNewCat}
              onAdd={() => addItem("categories", newCat)}
              onRemove={(v) => removeItem("categories", v)}
              placeholder="ej: OFICIAL 1ª"
            />
          </div>
        ) : null}

        {catalog && (
          <div className="mt-6 grid grid-cols-2 gap-3 text-xs text-slate-500">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <Building2 size={14} className="text-slate-400" />
              <span><b className="text-slate-700">{catalog.vehicles.length}</b> vehículos en flota · <a href="/fleet" className="text-[#007934] hover:underline">gestionar</a></span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
              <BadgeCheck size={14} className="text-slate-400" />
              <span><b className="text-slate-700">{catalog.personnel.length}</b> personas activas · <a href="/personnel" className="text-[#007934] hover:underline">gestionar</a></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CatalogList({ title, icon: Icon, items, newValue, setNewValue, onAdd, onRemove, placeholder }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-4 py-2.5 flex items-center gap-2 border-b border-slate-200">
        <Icon size={15} className="text-slate-500" />
        <span className="text-sm font-bold text-slate-700">{title}</span>
        <span className="ml-auto text-xs text-slate-400">{items.length} items</span>
      </div>
      <div className="p-3 flex gap-2 border-b border-slate-100">
        <input
          data-testid={`catalog-${title.toLowerCase()}-input`}
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") onAdd(); }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-[#007934]"
        />
        <button
          onClick={onAdd}
          data-testid={`catalog-${title.toLowerCase()}-add`}
          className="inline-flex items-center gap-1 px-3 py-2 bg-[#007934] hover:bg-[#005c27] text-white rounded-lg text-xs font-bold"
        >
          <Plus size={14} />
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-slate-400 italic px-4 py-6 text-center">Sin items todavía</p>
      ) : (
        <ul className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {items.map(it => (
            <li key={it} className="px-4 py-2 flex items-center justify-between hover:bg-slate-50 text-sm">
              <span className="text-slate-700">{it}</span>
              <button
                onClick={() => onRemove(it)}
                className="text-slate-300 hover:text-red-600 p-1"
                title="Eliminar"
              >
                <Trash2 size={13} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
